import { useCallback } from 'react';
import { FileDropzone } from '@components/FileDropzone/FileDropzone';
import { FileList } from '@components/FileList/FileList';
import { ConvertButton } from '@components/ConvertButton/ConvertButton';
import { DownloadButton, triggerDownload, triggerBlobDownload } from '@components/DownloadButton/DownloadButton';
import { ProgressIndicator } from '@components/ProgressIndicator/ProgressIndicator';
import { ResultsSummary } from '@components/ResultsSummary/ResultsSummary';
import { ConversionOptions } from '@components/ConversionOptions/ConversionOptions';
import { Notification, useNotification } from '@components/Notification/Notification';
import { useConversionStore } from '@stores/conversionStore';
import { useFileUpload } from '@hooks/useFileUpload';
import { useBulkConversion } from '@hooks/useBulkConversion';
import { generateOutputFilename } from '@services/converter';
import layout from '@/styles/PageLayout.module.css';
import styles from './Convert.module.css';

export function Convert() {
  const { items: notifications, dismiss, notify } = useNotification();

  const files = useConversionStore((s) => s.files);
  const addFiles = useConversionStore((s) => s.addFiles);
  const removeFile = useConversionStore((s) => s.removeFile);
  const clearFiles = useConversionStore((s) => s.clearFiles);

  const { validateBatch, createCubeFiles } = useFileUpload();
  const { progress, isConverting, result, convertAll, reset } = useBulkConversion();

  const handleFilesAdded = useCallback(
    (selectedFiles: File[]) => {
      const { valid, errors } = validateBatch(selectedFiles);
      if (errors.length > 0) {
        notify('warning', 'Some files were rejected', errors.slice(0, 3).join('. ') + (errors.length > 3 ? ` (+${errors.length - 3} more)` : ''));
      }
      if (valid.length > 0) {
        addFiles(createCubeFiles(valid));
      }
    },
    [validateBatch, createCubeFiles, addFiles, notify]
  );

  const handleConvert = useCallback(async () => { await convertAll(); }, [convertAll]);

  const handleClear = useCallback(() => { clearFiles(); reset(); }, [clearFiles, reset]);

  const handleDownload = useCallback(() => {
    if (!result) return;

    // Single file: download .xmp directly
    if (result.succeeded === 1 && files.length === 1) {
      const file = files[0];
      if (file?.result?.xmpContent) {
        const outputName = generateOutputFilename(file.filename);
        triggerDownload(file.result.xmpContent, outputName);
        notify('success', 'Download Started', outputName);
        return;
      }
    }

    // Multiple files: download ZIP
    if (result.zipBlob) {
      triggerBlobDownload(result.zipBlob, result.zipFilename);
      notify('success', 'Download Started', result.zipFilename);
    }
  }, [result, files, notify]);

  const isSingle = files.length === 1;
  const canConvert = files.length > 0 && !isConverting && !result;
  const showProgress = isConverting;
  const showResults = Boolean(result && !isConverting);
  const allFailed = result ? result.succeeded === 0 : false;
  const failureMessage = result && result.failed > 0
    ? `${result.failed} file${result.failed !== 1 ? 's' : ''} failed`
    : undefined;

  const downloadLabel = isSingle
    ? 'Download .xmp'
    : `Download ZIP (${result?.succeeded ?? 0} files)`;

  return (
    <div className={layout.container}>
      <Notification items={notifications} onDismiss={dismiss} />

      <div className={layout.pageHeader}>
        <p className={layout.eyebrow}>LUT → XMP Converter</p>
        <h1 className={layout.title}>Convert 3D LUTs to Lightroom Profiles</h1>
        <p className={layout.desc}>
          Drop one or more .cube files to convert them into .xmp profiles for Adobe Lightroom and Camera Raw.
          Everything runs in your browser — nothing is uploaded.
        </p>
      </div>

      <div className={styles.section}>
        {!isConverting && !result && (
          <FileDropzone onFilesAdded={handleFilesAdded} disabled={isConverting} multiple />
        )}

        {files.length > 1 && (
          <FileList files={files} onRemove={removeFile} disabled={isConverting} />
        )}

        {files.length > 0 && !isConverting && !result && (
          <ConversionOptions disabled={isConverting} defaultCollapsed />
        )}

        {showProgress && (
          <ProgressIndicator
            completed={progress.completed}
            total={progress.total}
            currentFile={progress.currentFile ?? undefined}
          />
        )}

        {showResults && result && (
          <ResultsSummary
            success={!allFailed}
            outputFilename={isSingle ? generateOutputFilename(files[0]?.filename ?? '') : result.zipFilename}
            warnings={[]}
            errorMessage={allFailed ? 'All files failed to convert.' : failureMessage}
          >
            {result.succeeded > 0 && (
              <DownloadButton
                onClick={handleDownload}
                label={downloadLabel}
                zipMode={!isSingle}
              />
            )}
          </ResultsSummary>
        )}

        <div className={styles.actions}>
          {canConvert && (
            <ConvertButton
              onClick={handleConvert}
              disabled={files.length === 0}
              loading={isConverting}
              label={isSingle ? 'Convert to .xmp' : `Convert ${files.length} files`}
            />
          )}
          {(files.length > 0 || result) && !isConverting && (
            <button className={styles.clearBtn} onClick={handleClear}>
              {result ? 'Start Over' : 'Clear All'}
            </button>
          )}
          {files.length > 0 && (
            <span className={styles.fileCount}>
              {files.length} file{files.length !== 1 ? 's' : ''} selected
              {result && ` · ${result.succeeded} succeeded, ${result.failed} failed`}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default Convert;
